import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit, Trash2, Mic, Upload, Video, Image, Check, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPodcasts = () => {
  const [podcasts, setPodcasts] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [editingPodcast, setEditingPodcast] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [videoFile, setVideoFile] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const videoInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const [formData, setFormData] = useState({
    title: '',
    guest: '',
    duration: '',
    audio_url: '',
    video_url: '',
    description: '',
    image_url: ''
  });

  useEffect(() => {
    fetchPodcasts();
  }, []);

  const fetchPodcasts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/podcasts`);
      setPodcasts(response.data);
    } catch (error) {
      toast.error('Error al cargar podcasts');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('session_token');
    setUploading(true);

    try {
      let videoUrl = formData.video_url;
      let imageUrl = formData.image_url;

      // Subir video si hay uno nuevo
      if (videoFile) {
        const videoFormData = new FormData();
        videoFormData.append('file', videoFile);
        videoFormData.append('title', formData.title);
        videoFormData.append('description', formData.description || '');
        videoFormData.append('category', 'podcast');
        videoFormData.append('levels', 'free');
        videoFormData.append('access_options', 'free');
        
        const videoResponse = await axios.post(
          `${BACKEND_URL}/api/content/upload`,
          videoFormData,
          { 
            headers: { 
              Authorization: `Bearer ${token}`,
              
            } 
          }
        );
        videoUrl = videoResponse.data.file_path;
      }

      // Subir imagen de portada si hay una nueva
      if (imageFile) {
        const imgFormData = new FormData();
        imgFormData.append('image', imageFile);
        
        const imgResponse = await axios.post(
          `${BACKEND_URL}/api/admin/site-config/upload-image`,
          imgFormData,
          { 
            headers: { 
              Authorization: `Bearer ${token}`,
              
            } 
          }
        );
        imageUrl = imgResponse.data.url;
      }

      const podcastData = { 
        ...formData, 
        video_url: videoUrl,
        image_url: imageUrl 
      };

      if (editingPodcast) {
        await axios.put(
          `${BACKEND_URL}/api/admin/podcasts/${editingPodcast.podcast_id}`,
          podcastData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Podcast actualizado');
      } else {
        await axios.post(
          `${BACKEND_URL}/api/admin/podcasts`,
          podcastData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Podcast creado');
      }
      setIsOpen(false);
      resetForm();
      fetchPodcasts();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al guardar podcast');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (podcastId) => {
    if (!window.confirm('¿Estás segura de eliminar este podcast?')) return;

    const token = localStorage.getItem('session_token');
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/podcasts/${podcastId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Podcast eliminado');
      fetchPodcasts();
    } catch (error) {
      toast.error('Error al eliminar podcast');
    }
  };

  const handleEdit = (podcast) => {
    setEditingPodcast(podcast);
    setVideoFile(null);
    setImageFile(null);
    setFormData({
      title: podcast.title,
      guest: podcast.guest,
      duration: podcast.duration,
      audio_url: podcast.audio_url || '',
      video_url: podcast.video_url || '',
      description: podcast.description || '',
      image_url: podcast.image_url || ''
    });
    setIsOpen(true);
  };

  const resetForm = () => {
    setEditingPodcast(null);
    setVideoFile(null);
    setImageFile(null);
    setFormData({
      title: '',
      guest: '',
      duration: '',
      audio_url: '',
      video_url: '',
      description: '',
      image_url: ''
    });
  };

  return (
    <div className="min-h-screen bg-[#09090b] py-12">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex justify-between items-center mb-12">
          <h1 className="text-4xl font-bold" style={{fontFamily: 'Poppins, sans-serif'}} data-testid="admin-podcasts-title">
            Gestión de Podcasts
          </h1>
          <Dialog open={isOpen} onOpenChange={(open) => { setIsOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-black hover:bg-zinc-800 text-white rounded-full px-6 py-3" data-testid="create-podcast-button">
                <Plus className="w-5 h-5 mr-2" />
                Crear Podcast
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingPodcast ? 'Editar Podcast' : 'Crear Nuevo Podcast'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Título</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label>Invitado</Label>
                  <Input
                    value={formData.guest}
                    onChange={(e) => setFormData({...formData, guest: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label>Duración (ej: 45:30)</Label>
                  <Input
                    value={formData.duration}
                    onChange={(e) => setFormData({...formData, duration: e.target.value})}
                    placeholder="00:00"
                    required
                  />
                </div>
                <div>
                  <Label>Descripción</Label>
                  <textarea
                    className="w-full p-2 border rounded-md"
                    rows={3}
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                  />
                </div>

                {/* Subir Video */}
                <div>
                  <Label>🎬 Video del Podcast</Label>
                  {formData.video_url && !videoFile && (
                    <div className="mb-2 flex items-center gap-3 bg-green-50 rounded-lg p-2">
                      <Video className="w-6 h-6 text-green-600" />
                      <span className="text-xs text-green-700 truncate flex-1">Video actual cargado</span>
                      <a 
                        href={formData.video_url.startsWith('/api') ? `${BACKEND_URL}${formData.video_url}` : formData.video_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-green-600 text-xs hover:underline"
                      >
                        Ver
                      </a>
                    </div>
                  )}
                  <div 
                    onClick={() => videoInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-colors ${
                      videoFile ? 'border-green-300 bg-green-50' : 'border-zinc-700 hover:border-pink-300 hover:bg-pink-50'
                    }`}
                  >
                    <input
                      ref={videoInputRef}
                      type="file"
                      accept="video/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) setVideoFile(file);
                      }}
                      className="hidden"
                    />
                    {videoFile ? (
                      <div className="flex items-center justify-center gap-3">
                        <Check className="w-6 h-6 text-green-600" />
                        <div className="text-left">
                          <p className="font-medium text-green-700 text-sm">{videoFile.name}</p>
                          <p className="text-xs text-green-600">{formatFileSize(videoFile.size)}</p>
                        </div>
                        <button 
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setVideoFile(null); }}
                          className="ml-2 text-red-500 hover:text-red-700"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <Video className="w-8 h-8 text-zinc-400 mx-auto mb-2" />
                        <p className="text-sm text-zinc-400">
                          {formData.video_url ? 'Cambiar video' : 'Subir video'}
                        </p>
                        <p className="text-xs text-zinc-400 mt-1">MP4, MOV, WebM (máx 500MB)</p>
                      </>
                    )}
                  </div>
                </div>

                {/* Subir Imagen de Portada */}
                <div>
                  <Label>🖼️ Imagen de Portada</Label>
                  {formData.image_url && !imageFile && (
                    <div className="mb-2 flex items-center gap-3 bg-blue-50 rounded-lg p-2">
                      <img 
                        src={formData.image_url.startsWith('/api') ? `${BACKEND_URL}${formData.image_url}` : formData.image_url}
                        alt="Portada actual"
                        className="w-16 h-16 object-cover rounded"
                      />
                      <span className="text-xs text-blue-700">Portada actual</span>
                    </div>
                  )}
                  <div 
                    onClick={() => imageInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-colors ${
                      imageFile ? 'border-blue-300 bg-blue-50' : 'border-zinc-700 hover:border-blue-300 hover:bg-blue-50'
                    }`}
                  >
                    <input
                      ref={imageInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) setImageFile(file);
                      }}
                      className="hidden"
                    />
                    {imageFile ? (
                      <div className="flex items-center justify-center gap-3">
                        <Check className="w-6 h-6 text-blue-600" />
                        <div className="text-left">
                          <p className="font-medium text-blue-700 text-sm">{imageFile.name}</p>
                          <p className="text-xs text-blue-600">{formatFileSize(imageFile.size)}</p>
                        </div>
                        <button 
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setImageFile(null); }}
                          className="ml-2 text-red-500 hover:text-red-700"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <Image className="w-8 h-8 text-zinc-400 mx-auto mb-2" />
                        <p className="text-sm text-zinc-400">
                          {formData.image_url ? 'Cambiar portada' : 'Subir portada'}
                        </p>
                        <p className="text-xs text-zinc-400 mt-1">JPG, PNG o WebP (máx 5MB)</p>
                      </>
                    )}
                  </div>
                </div>

                <div>
                  <Label>URL de Audio (Spotify, SoundCloud - Opcional)</Label>
                  <Input
                    value={formData.audio_url}
                    onChange={(e) => setFormData({...formData, audio_url: e.target.value})}
                    placeholder="https://open.spotify.com/..."
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => { setIsOpen(false); resetForm(); }}>
                    Cancelar
                  </Button>
                  <Button 
                    type="submit" 
                    className="bg-black hover:bg-zinc-800"
                    disabled={uploading}
                  >
                    {uploading ? 'Subiendo...' : (editingPodcast ? 'Actualizar' : 'Crear')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {podcasts.map((podcast) => (
            <div key={podcast.podcast_id} className="workout-card rounded-3xl overflow-hidden">
              {podcast.image_url && (
                <img 
                  src={podcast.image_url.startsWith('/api') ? `${BACKEND_URL}${podcast.image_url}` : podcast.image_url} 
                  alt={podcast.title} 
                  className="w-full h-48 object-cover" 
                />
              )}
              <div className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-pink-600">EPISODIO</span>
                  <span className="text-sm text-zinc-400">{podcast.duration}</span>
                </div>
                <h3 className="text-xl font-bold mb-2" style={{fontFamily: 'Poppins, sans-serif'}}>{podcast.title}</h3>
                <p className="text-sm text-zinc-400 mb-4">{podcast.guest}</p>
                {podcast.description && (
                  <p className="text-sm text-zinc-400 mb-4 line-clamp-2">{podcast.description}</p>
                )}
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleEdit(podcast)}
                    variant="outline"
                    className="flex-1"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Editar
                  </Button>
                  <Button
                    onClick={() => handleDelete(podcast.podcast_id)}
                    variant="destructive"
                    className="flex-1"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Eliminar
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminPodcasts;
